import type { GetStaticProps, InferGetServerSidePropsType, InferGetStaticPropsType } from 'next';
import { NextSeo } from 'next-seo';
import React, { useMemo } from 'react';
import type { ReactElement } from 'react';
import styled, { css } from 'styled-components';

import { richTextToReactNode } from '@seuk/cms-api-client';
import { Container, Main, RichTextWrapper } from '@seuk/design-system/atoms';
import { Breadcrumbs } from '@seuk/design-system/molecules';
import { MarketingCards, TextSection } from '@seuk/design-system/organisms';
import type { CardInterface } from '@seuk/design-system/organisms';
import { DateFormat, formatDate } from '@seuk/util';

import { Hero } from '../../../components';

import {
  ArticleHeaderContainer,
  ArticleSubtitle,
  ArticleTitle,
  Categories,
  CategoryBadge,
  ContactUsContainer,
  TextColumn,
} from './article.styles';
import { ArticleSection } from './components/article-child-section';
import type { IArticlePageProps } from './types';

const ARTICLES_PATH = '/energy-insights/articles';

const SectionTitle = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.heading[2].demi};
    margin-top: 0;
    margin-bottom: ${theme.spacing(12)};
    color: ${theme.palette.focus.strong};
    padding: ${theme.spacing(8)};
    text-align: center;
  `}
`;

function ArticlePage<GetServerSideProps extends GetStaticProps<IArticlePageProps>>({
  date,
  heroImage,
  heroImageAltText,
  articleTitle,
  articleSubtitle,
  articleDescription,
  readingTime,
  sections,
  seo,
  categories,
  dateVisible,
  relatedArticles,
}: InferGetServerSidePropsType<GetServerSideProps>): ReactElement {
  const description = richTextToReactNode(articleDescription);

  const breadcrumbItems = React.useMemo(() => {
    return [
      { label: 'Home', href: '/' },
      { label: 'Energy insights', href: '/energy-insights' },
      { label: articleTitle },
    ];
  }, [articleTitle]);

  const dateValue = useMemo(() => {
    return dateVisible && date ? formatDate(new Date(date), DateFormat.full) : null;
  }, [dateVisible, date]);

  return (
    <>
      <NextSeo title={seo?.title} description={seo?.description} noindex={seo?.noindex} nofollow={seo?.nofollow} />
      <Main data-testid="article-page">
        <Hero heroImage={heroImage} viewHeight={25} heroAltText={heroImageAltText} />

        <Container>
          <ArticleHeaderContainer>
            <TextColumn>
              <Breadcrumbs items={breadcrumbItems} backHref="/energy-insights" />
              <Categories flexWrap="wrap">
                {categories.map((category) => (
                  <CategoryBadge key={category}>{category}</CategoryBadge>
                ))}
              </Categories>
              <ArticleTitle>{articleTitle}</ArticleTitle>
              <p>
                {dateValue}
                {dateValue && readingTime && ' | '}
                {readingTime && `${readingTime} minute${readingTime > 1 ? 's' : ''}`}
              </p>
              <ArticleSubtitle>{articleSubtitle}</ArticleSubtitle>
              <RichTextWrapper>{description}</RichTextWrapper>
            </TextColumn>
          </ArticleHeaderContainer>
          {sections?.map((section) => (
            <ArticleSection key={section.id} {...section} />
          ))}
          {!!relatedArticles?.length && (
            <>
              <SectionTitle>Related articles</SectionTitle>
              <MarketingCards
                showTitle
                titleAsLink
                columnsAmount={3}
                cards={relatedArticles.map(
                  (article) =>
                    ({
                      image: article.cardImageAsset,
                      imageAltText: article.cardImageAltText,
                      categories: article.categories,
                      title: article.articleTitle,
                      date: article.dateVisible ? article.date : null,
                      readingTime: article.readingTime,
                      description: article.articleSubtitle,
                      button: { title: 'Read more', style: 'Secondary', href: `${ARTICLES_PATH}/${article.slug}` },
                    } as CardInterface)
                )}
              />
            </>
          )}
          <ContactUsContainer>
            <TextSection
              title="Speak to our energy experts"
              description="We're here to help"
              iconPosition="Left-top"
              imagePosition="Left"
              buttons={[{ href: '/contact', title: 'Contact us' }]}
              alignItems="center"
            />
          </ContactUsContainer>
        </Container>
      </Main>
    </>
  );
}

export function makeArticlePage(): React.FC<InferGetStaticPropsType<GetStaticProps<IArticlePageProps>>> {
  return function CustomEnergyInsightsPage(props) {
    return <ArticlePage {...props} />;
  };
}
